import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';

import { getCookieNameSuffix } from 'utils/cookies';

export interface AuthenticatedEncryptedEnvelope {
  version: 1;
  algorithm: 'AES-GCM';
  iv: string;
  ciphertext: string;
}

const ENCRYPTION_VERSION = 1;
const PBKDF2_ITERATIONS = 100000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const toBase64 = (bytes: Uint8Array) => {
  return window.btoa(String.fromCharCode(...bytes));
};

const fromBase64 = (value: string) => Uint8Array.from(window.atob(value), (char) => char.charCodeAt(0));

const sortObjectKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeys);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortObjectKeys((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }

  return value;
};

const getAccessToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const cookies = new Cookies(null, { path: '/' });
  return cookies.get(`AF_ATHP${getCookieNameSuffix()}`) || null;
};

const getEncryptionSeed = () => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }

  try {
    const decoded = jwtDecode(accessToken);
    const { exp: _exp, iat: _iat, nbf: _nbf, jti: _jti, ...stableClaims } = decoded;

    return JSON.stringify(sortObjectKeys(stableClaims));
  } catch {
    return null;
  }
};

const deriveEncryptionKey = async (namespace: string) => {
  const seed = getEncryptionSeed();
  if (!seed || !window.crypto?.subtle) {
    return null;
  }

  const keyMaterial = await window.crypto.subtle.importKey('raw', encoder.encode(seed), 'PBKDF2', false, ['deriveKey']);

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(namespace),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

export const isAuthenticatedEncryptedEnvelope = (value: unknown): value is AuthenticatedEncryptedEnvelope => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const envelope = value as Partial<AuthenticatedEncryptedEnvelope>;
  return Boolean(
    envelope.version === ENCRYPTION_VERSION &&
    envelope.algorithm === 'AES-GCM' &&
    typeof envelope.iv === 'string' &&
    typeof envelope.ciphertext === 'string'
  );
};

export const encryptForCurrentUser = async ({ namespace, plaintext }: { namespace: string; plaintext: string }) => {
  const key = await deriveEncryptionKey(namespace);
  if (!key) {
    return null;
  }

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoder.encode(plaintext));

  return JSON.stringify({
    version: ENCRYPTION_VERSION,
    algorithm: 'AES-GCM',
    iv: toBase64(iv),
    ciphertext: toBase64(new Uint8Array(ciphertext))
  } satisfies AuthenticatedEncryptedEnvelope);
};

export const decryptForCurrentUser = async ({ namespace, raw }: { namespace: string; raw: string | null }) => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!isAuthenticatedEncryptedEnvelope(parsed)) {
      return null;
    }

    const key = await deriveEncryptionKey(namespace);
    if (!key) {
      return null;
    }

    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(parsed.iv) },
      key,
      fromBase64(parsed.ciphertext)
    );

    return decoder.decode(decrypted);
  } catch {
    return null;
  }
};
